import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { Box, Divider, Drawer, IconButton, Stack, Typography } from '@mui/material';
import { AgentActivityLogDocumentType } from '@trnsact/trnsact-shared-types';
import { ModalsKeys } from 'global';
import { useModal } from 'hooks/useModal';
import { TriggerDetailsSection } from './TriggerDetailsSection';
import { SystemInfoSection } from './SystemInfoSection';
import { MatchingDetailsSection } from './MatchingDetailsSection';
import { ParsedDataSection } from './ParsedDataSection';
import { CreditAppExtractionSection } from './CreditAppExtractionSection';

const PROCESS_TYPE_CONFIG: Record<string, { label: string; bg: string; color: string }> = {
  email_ingestion: { label: 'Magic Email Parsing', bg: '#f3e5f5', color: '#9c27b0' },
  doc_parsing: { label: 'Document Parsing', bg: '#e8eaf6', color: '#3f51b5' },
  subprime_agent: { label: 'Qualification Assistant', bg: '#e0f2f1', color: '#00897b' },
};

const DOCUMENT_TYPE_CONFIG: Record<string, { label: string; bg: string; color: string }> = {
  credit_decision: { label: 'Credit Decision', bg: '#e3f2fd', color: '#1565c0' },
  credit_application: { label: 'Credit Application', bg: '#e8f5e9', color: '#2e7d32' },
};

const OUTCOME_CONFIG: Record<string, { label: string; color: string; icon: React.ReactElement }> = {
  success: { label: 'Success', color: '#66bb6a', icon: <CheckCircleIcon sx={{ fontSize: 18, color: '#66bb6a' }} /> },
  failed: { label: 'Failed', color: '#ff5252', icon: <ErrorIcon sx={{ fontSize: 18, color: '#ff5252' }} /> },
};

export const ActivityDetailsDialog = () => {
  const { isOpen, handleClose, data } = useModal(ModalsKeys.AssistantLogActivityDetailsDialog);

  const processTypeConfig = data?.processType ? PROCESS_TYPE_CONFIG[data.processType] : null;
  const documentTypeConfig = data?.documentType ? DOCUMENT_TYPE_CONFIG[data.documentType] : null;
  const outcomeConfig = data?.outcome ? OUTCOME_CONFIG[data.outcome] : null;
  const isCreditApp = data?.documentType === AgentActivityLogDocumentType.CreditApplication;

  return (
    <Drawer anchor="right" open={isOpen} onClose={handleClose} PaperProps={{ sx: { width: 480 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
        <Typography variant="h6">Activity Details</Typography>
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider />

      <Box sx={{ p: 3, overflow: 'auto', flex: 1 }}>
        <Stack spacing={3}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            {processTypeConfig && (
              <Box sx={{ display: 'inline-flex', px: 1, py: 0.25, borderRadius: '2px', bgcolor: processTypeConfig.bg }}>
                <Typography variant="body2" sx={{ color: processTypeConfig.color, whiteSpace: 'nowrap' }}>
                  {processTypeConfig.label}
                </Typography>
              </Box>
            )}

            {documentTypeConfig && (
              <Box
                sx={{ display: 'inline-flex', px: 1, py: 0.25, borderRadius: '2px', bgcolor: documentTypeConfig.bg }}
              >
                <Typography variant="body2" sx={{ color: documentTypeConfig.color, whiteSpace: 'nowrap' }}>
                  {documentTypeConfig.label}
                </Typography>
              </Box>
            )}

            {outcomeConfig && (
              <Stack direction="row" spacing={0.25} alignItems="center">
                {outcomeConfig.icon}
                <Typography variant="subtitle2" sx={{ color: outcomeConfig.color }}>
                  {outcomeConfig.label}
                </Typography>
              </Stack>
            )}
          </Stack>

          <TriggerDetailsSection
            trigger={data?.trigger}
            payload={data?.payload}
            agentActivityLogId={data?.agentActivityLogId}
          />

          <SystemInfoSection
            outcome={data?.outcome}
            trigger={data?.trigger}
            payload={data?.payload}
            error={data?.error}
            associations={data?.associations}
            documentType={data?.documentType}
          />

          {isCreditApp ? (
            <CreditAppExtractionSection payload={data?.payload} />
          ) : (
            <>
              <MatchingDetailsSection payload={data?.payload} />
              <ParsedDataSection payload={data?.payload} />
            </>
          )}
        </Stack>
      </Box>
    </Drawer>
  );
};
